/**
 * ManualPaymentModal — record a manual/offline registration payment.
 *
 * Used by:
 *   - AdminRegistrationsList Review modal ("Record Payment" button)
 *   - AdminPaymentsPage ("+ Record Manual Payment" button)
 *
 * Finance-role only (super_admin + treasurer). Backend enforces this
 * — this modal never renders itself for other roles because the parent
 * component already gates on `isFinanceRole`.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, DollarSign, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'cash_app', label: 'Cash App' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'venmo', label: 'Venmo' },
  { value: 'check', label: 'Check' },
  { value: 'external_card', label: 'External Card' },
  { value: 'other', label: 'Other' },
];

const PRICE_REASONS = [
  { value: 'full_price', label: 'Full Price' },
  { value: 'early_bird', label: 'Early Bird' },
  { value: 'sibling_discount', label: 'Sibling Discount' },
  { value: 'scholarship', label: 'Scholarship' },
  { value: 'staff_child', label: 'Staff Child' },
  { value: 'promotional_rate', label: 'Promotional Rate' },
  { value: 'registration_correction', label: 'Registration Correction' },
  { value: 'custom', label: 'Custom' },
  { value: 'other', label: 'Other' },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function ManualPaymentModal({ open, onClose, registration, onRecorded }) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [methodNote, setMethodNote] = useState('');
  const [paymentDate, setPaymentDate] = useState(todayISO());
  const [referenceNumber, setReferenceNumber] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [payerNameOverride, setPayerNameOverride] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [sendReceipt, setSendReceipt] = useState(true);
  const [receiptEmailOverride, setReceiptEmailOverride] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [overpaymentWarning, setOverpaymentWarning] = useState(null);

  // Adjust final price add-on (finance-only, optional).
  const [adjustPrice, setAdjustPrice] = useState(false);
  const [newFinalPrice, setNewFinalPrice] = useState('');
  const [priceReason, setPriceReason] = useState('early_bird');
  const [priceNote, setPriceNote] = useState('');

  useEffect(() => {
    if (open) {
      // Reset form on open.
      setAmount('');
      setMethod('cash');
      setMethodNote('');
      setPaymentDate(todayISO());
      setReferenceNumber('');
      setCheckNumber('');
      setPayerNameOverride('');
      setInternalNote('');
      setSendReceipt(true);
      setReceiptEmailOverride('');
      setOverpaymentWarning(null);
      setAdjustPrice(false);
      setNewFinalPrice('');
      setPriceReason('early_bird');
      setPriceNote('');
    }
  }, [open]);

  if (!open || !registration) return null;

  const standardPrice = Number(registration.standard_amount || registration.total_amount || 0);
  const total = Number(registration.total_amount || 0);
  const paid = Number(registration.amount_paid || 0);
  const remaining = Math.max(0, total - paid);
  const amountEntered = amount && Number(amount) > 0;
  const priceProvided = adjustPrice && newFinalPrice !== '' && Number(newFinalPrice) >= 0;
  const priceIsChange = priceProvided && Math.abs(Number(newFinalPrice) - total) > 0.001;

  const submit = async (requireOverpaymentAck = false) => {
    // Allow: payment-only, price-only, or both.
    if (!amountEntered && !priceIsChange) {
      toast.error('Enter a payment amount, adjust the price, or both.');
      return;
    }
    if (amountEntered && method === 'other' && !methodNote.trim()) {
      toast.error('Describe the payment method when selecting Other');
      return;
    }
    if (adjustPrice && priceReason === 'other' && !priceNote.trim()) {
      toast.error('Describe the price reason when selecting Other');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        require_overpayment_ack: requireOverpaymentAck,
      };
      if (amountEntered) {
        payload.amount = Number(amount);
        payload.method = method;
        payload.method_note = methodNote || undefined;
        payload.payment_date = paymentDate || undefined;
        payload.reference_number = referenceNumber || undefined;
        payload.check_number = checkNumber || undefined;
        payload.payer_name_override = payerNameOverride || undefined;
        payload.internal_note = internalNote || undefined;
        payload.send_receipt = sendReceipt;
        payload.receipt_email_override = receiptEmailOverride || undefined;
      }
      if (priceIsChange) {
        payload.adjust_final_price = true;
        payload.new_final_price = Number(newFinalPrice);
        payload.price_reason = priceReason;
        payload.price_note = priceNote || undefined;
      }
      const res = await axios.post(
        `${API_URL}/api/admin/registrations/${registration.registration_id}/payments`,
        payload,
        { withCredentials: true }
      );
      if (res.data?.warning === 'OVERPAYMENT' && !res.data?.recorded) {
        setOverpaymentWarning(res.data);
        setSubmitting(false);
        return;
      }
      const status = res.data?.registration?.payment_status;
      const madePriceChange = !!res.data?.price_adjustment;
      const madePayment = !!res.data?.payment;
      if (madePayment && madePriceChange) {
        toast.success(`Price adjusted and payment recorded — registration ${status || 'updated'}.`);
      } else if (madePriceChange) {
        toast.success('Final price adjusted.');
      } else if (status === 'paid') {
        toast.success('Payment recorded — registration marked Paid.');
      } else if (status === 'overpaid') {
        toast.success('Payment recorded — registration marked Overpaid.');
      } else {
        toast.success('Payment recorded — partial balance updated.');
      }
      setOverpaymentWarning(null);
      if (onRecorded) onRecorded(res.data);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to record payment';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         data-testid="manual-payment-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b px-6 py-4 sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-navy" />
            <h2 className="text-lg font-bold text-navy">Record Manual Payment</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800"
                  data-testid="manual-payment-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Registration summary */}
          <div className="bg-gray-50 rounded-lg p-3 grid grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-gray-500 text-xs">Total</div>
              <div className="font-bold text-navy">${total.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-gray-500 text-xs">Paid to date</div>
              <div className="font-bold text-green-600">${paid.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-gray-500 text-xs">Remaining</div>
              <div className="font-bold text-red-600" data-testid="mp-remaining">${remaining.toFixed(2)}</div>
            </div>
          </div>

          {overpaymentWarning && (
            <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 flex gap-2"
                 data-testid="overpayment-warning">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <div className="font-semibold">Overpayment confirmation required</div>
                <div>{overpaymentWarning.message}</div>
                <div className="mt-2 flex gap-2">
                  <Button
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => submit(true)}
                    disabled={submitting}
                    data-testid="confirm-overpayment-btn"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Confirm overpayment
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setOverpaymentWarning(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Registration Price section (optional adjust). */}
          <div className="border border-gray-200 rounded-lg p-3" data-testid="price-section">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Registration Price</div>
                <div className="text-sm text-gray-700">
                  Standard: <span className="font-mono">${standardPrice.toFixed(2)}</span>
                  <span className="mx-2 text-gray-400">·</span>
                  Current final: <span className="font-mono font-semibold text-navy">${total.toFixed(2)}</span>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={adjustPrice}
                  onChange={(e) => setAdjustPrice(e.target.checked)}
                  data-testid="mp-adjust-price-toggle"
                />
                Adjust final price
              </label>
            </div>
            {adjustPrice && (
              <div className="mt-2 grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-600 block mb-1">New final price *</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newFinalPrice}
                    onChange={(e) => setNewFinalPrice(e.target.value)}
                    placeholder="e.g. 225.00"
                    data-testid="mp-new-final-price"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Reason *</label>
                  <select
                    value={priceReason}
                    onChange={(e) => setPriceReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    data-testid="mp-price-reason"
                  >
                    {PRICE_REASONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-gray-600 block mb-1">
                    Internal note {priceReason === 'other' ? '*' : '(optional)'}
                  </label>
                  <Input
                    value={priceNote}
                    onChange={(e) => setPriceNote(e.target.value)}
                    placeholder={priceReason === 'other' ? 'Describe the reason' : 'e.g., Late-fee waived after phone call'}
                    data-testid="mp-price-note"
                  />
                </div>
              </div>
            )}
          </div>


          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                Amount {adjustPrice ? '(optional if adjusting price only)' : '*'}
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                data-testid="mp-amount"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Date received *</label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                data-testid="mp-date"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Method *</label>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="mp-method"
              >
                {METHODS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                {method === 'check' ? 'Check number' : 'Reference number'}
              </label>
              <Input
                value={method === 'check' ? checkNumber : referenceNumber}
                onChange={(e) => (method === 'check' ? setCheckNumber(e.target.value) : setReferenceNumber(e.target.value))}
                placeholder={method === 'check' ? '#1234' : 'Confirmation / txn ID'}
                data-testid="mp-reference"
              />
            </div>
          </div>

          {method === 'other' && (
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Describe method *</label>
              <Input
                value={methodNote}
                onChange={(e) => setMethodNote(e.target.value)}
                placeholder="e.g., Money order, bank wire, gift card…"
                data-testid="mp-method-note"
              />
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Payer name (if different)</label>
            <Input
              value={payerNameOverride}
              onChange={(e) => setPayerNameOverride(e.target.value)}
              placeholder="Grandparent, family friend, etc."
              data-testid="mp-payer-override"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Internal note (never on receipt)</label>
            <textarea
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="Received at practice, envelope from grandma, etc."
              data-testid="mp-internal-note"
            />
          </div>

          <div className="border-t pt-3">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={sendReceipt}
                onChange={(e) => setSendReceipt(e.target.checked)}
                data-testid="mp-send-receipt"
              />
              Send receipt email to parent
            </label>
            {sendReceipt && (
              <div className="mt-2">
                <label className="text-xs text-gray-500 block mb-1">Receipt email override (optional)</label>
                <Input
                  value={receiptEmailOverride}
                  onChange={(e) => setReceiptEmailOverride(e.target.value)}
                  placeholder="Leave blank to use parent email on file"
                  data-testid="mp-receipt-email-override"
                />
              </div>
            )}
          </div>
        </div>

        <div className="border-t px-6 py-4 flex justify-end gap-2 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            className="bg-navy hover:bg-navy/90 text-white"
            onClick={() => submit(false)}
            disabled={submitting || !!overpaymentWarning}
            data-testid="mp-submit"
          >
            {submitting ? 'Saving…' : (amountEntered && priceIsChange ? 'Save Price & Record Payment' : priceIsChange ? 'Save Price Adjustment' : 'Record Payment')}
          </Button>
        </div>
      </div>
    </div>
  );
}
